import gsap from 'gsap'
import App from '../App'

export default class LightBox {
  constructor({ gallery } = {}) {
    this.gallery = gallery
    this.imagesWrappers = []
    this.imagesReverses = []
    this.images = []
    this.backgrounds = []

    this.getElems()
    this.addImageElements()
    this.setDefaultVariables()
    this.bindMethods()
    this.addEvents()
  }

  getElems() {
    this.lightBox = document.body.querySelector('.lightbox')
    this.backgroundWrapper = this.lightBox.querySelector('.lightbox__backgrounds')
    this.image = this.lightBox.querySelector('.lightbox__images')
    this.pagerPrev = this.lightBox.querySelector('.lightbox__pager--prev')
    this.pagerNext = this.lightBox.querySelector('.lightbox__pager--next')
    this.pagerClose = this.lightBox.querySelector('.lightbox__close')
  }

  setDefaultVariables() {
    this.currentZIndex = 1

    this.imageWidth = this.image.offsetWidth
    this.imageHeight = this.image.offsetHeight

    this.currentIndex = null
    this.currentImage = null

    this.setDefaultImage()
  }

  setDefaultImage() {
    gsap.set(this.images, { scale: 1.1 })
    gsap.set(this.imagesWrappers, { x: '100%' })
    gsap.set(this.imagesReverses, { x: '-100%' })
  }

  addImageElements() {
    for (let i = 0; i < this.gallery.length; i++) {
      const imageWrapper = document.createElement('div')
      const background = document.createElement('div')
      const imageReverse = document.createElement('div')
      const imageBackground = document.createElement('img')
      const image = document.createElement('img')

      imageBackground.src = this.gallery[i]
      image.src = this.gallery[i]
      background.classList.add('lightbox__background')
      imageReverse.classList.add('lightbox__image-reverse')
      imageWrapper.classList.add('lightbox__image')

      imageReverse.appendChild(image)
      background.appendChild(imageBackground)
      imageWrapper.appendChild(imageReverse)
      this.backgroundWrapper.appendChild(background)
      this.image.appendChild(imageWrapper)

      this.imagesReverses.push(imageReverse)
      this.imagesWrappers.push(imageWrapper)
      this.backgrounds.push(background)
      this.images.push(image)
    }
  }

  bindMethods() {
    this.onImageClick = this.onImageClick.bind(this)
    this.close = this.close.bind(this)
    this.showPrev = this.showPrev.bind(this)
    this.showNext = this.showNext.bind(this)
    this.onNextPagerClick = this.onNextPagerClick.bind(this)
    this.onPrevPagerClick = this.onPrevPagerClick.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onMouseLeave = this.onMouseLeave.bind(this)
  }

  addEvents() {
    this.image.addEventListener('mousemove', this.onMouseMove)
    this.image.addEventListener('mouseleave', this.onMouseLeave)
    this.image.addEventListener('click', this.onImageClick)
    this.pagerPrev.addEventListener('click', this.onPrevPagerClick)
    this.pagerNext.addEventListener('click', this.onNextPagerClick)
    this.pagerClose.addEventListener('click', this.close)
  }

  onImageClick() {
    if (this.prevPagerIsActive) this.showPrev()
    else if (this.nextPagerIsActive) this.showNext()
  }

  onMouseLeave() {
    if (App.isMobile) return

    this.prevPagerIsActive && this.hidePrevPreviewSlide()
    this.nextPagerIsActive && this.hideNextPreviewSlide()
    this.image.classList.remove('pointer')
  }

  onMouseMove(e) {
    if (App.isMobile) return

    this.currentPosEvent = e

    if (e.offsetX <= this.imageWidth * 0.48 && !this.prevPagerIsActive) {
      this.prevPagerIsActive = true
      this.nextPagerIsActive = false
      this.image.classList.add('pointer')
      this.showPrevPreviewSlide()
    } else if (e.offsetX >= this.imageWidth * 0.52 && !this.nextPagerIsActive) {
      this.prevPagerIsActive = false
      this.nextPagerIsActive = true
      this.image.classList.add('pointer')
      this.showNextPreviewSlide()
    } else if (e.offsetX > this.imageWidth * 0.48 && e.offsetX < this.imageWidth * 0.52) {
      this.image.classList.remove('pointer')
      this.prevPagerIsActive && this.hidePrevPreviewSlide()
      this.nextPagerIsActive && this.hideNextPreviewSlide()
    }
  }

  showPrevPreviewSlide() {
    this.currentZIndex ++
    this.imagesWrappers[this.prevIndex].style.zIndex = this.currentZIndex

    gsap.set(this.images[this.prevIndex], { scale: 1.1 })
    gsap.fromTo(this.imagesWrappers[this.prevIndex], { x: '-100%' }, {
      x: '-100%',
      duration: 0.3,
      ease: 'power2.out'
    })

    gsap.fromTo(this.imagesReverses[this.prevIndex], { x: '100%' }, {
      x: '100%',
      duration: 0.3,
      ease: 'power2.out'
    })

    App.cursor.scaleTo(100)
    App.cursor.cursor.classList.add('arrow-left')
  }

  onPrevPagerClick() {
    gsap.set(this.images[this.prevIndex], { scale: 1.1 })
    gsap.set(this.imagesWrappers[this.prevIndex], { x: '-100%' })
    gsap.set(this.imagesReverses[this.prevIndex], { x: '100%' })
    
    this.showPrev()
  }

  onNextPagerClick() {
    gsap.set(this.images[this.nextIndex], { scale: 1.1 })
    gsap.set(this.imagesWrappers[this.nextIndex], { x: '100%' })
    gsap.set(this.imagesReverses[this.nextIndex], { x: '-100%' })

    this.showNext()
  }

  showNextPreviewSlide() {
    this.currentZIndex ++
    this.imagesWrappers[this.nextIndex].style.zIndex = this.currentZIndex

    gsap.set(this.images[this.nextIndex], { scale: 1.1 })
    gsap.fromTo(this.imagesWrappers[this.nextIndex], { x: '100%' }, {
      x: '100%',
      duration: 0.3,
      ease: 'power2.out'
    })

    gsap.fromTo(this.imagesReverses[this.nextIndex], { x: '-100%' }, {
      x: '-100%',
      duration: 0.3,
      ease: 'power2.out'
    })

    App.cursor.scaleTo(100)
    App.cursor.cursor.classList.add('arrow-right')
  }

  hidePrevPreviewSlide() {
    this.prevPagerIsActive = false
    
    App.cursor.resetScale()
    App.cursor.cursor.classList.remove('arrow-left')
  }

  hideNextPreviewSlide() {
    this.nextPagerIsActive = false
    
    App.cursor.resetScale()
    App.cursor.cursor.classList.remove('arrow-right')
  }

  open() {
    this.setDefaultImage()
    this.lightBox.classList.add('open')
    App.updateCurtains = false
    this.isOpen = true
  }

  close() {
    this.lightBox.classList.remove('open')
    App.updateCurtains = true
    this.isOpen = false
  }

  showAtIndex(i) {
    if (this.currentIndex) this.oldBackground = this.backgrounds[this.currentIndex]

    this.currentIndex = i
    this.prevIndex = this.currentIndex === 0 ? this.gallery.length - 1 : this.currentIndex - 1
    this.nextIndex = this.currentIndex === this.gallery.length - 1 ? 0 : this.currentIndex + 1

    this.currentBackgroud = this.backgrounds[this.currentIndex]

    this.updateImages()
  }

  showPrev() {
    if (!this.isOpen) return

    this.oldBackground = this.backgrounds[this.currentIndex]

    this.nextIndex = this.currentIndex
    this.currentIndex = this.currentIndex === 0 ? this.gallery.length - 1 : this.currentIndex - 1
    this.prevIndex = this.currentIndex === 0 ? this.gallery.length - 1 : this.currentIndex - 1

    this.currentBackgroud = this.backgrounds[this.currentIndex]

    this.images[this.nextIndex].style.display = 'none'

    this.updateImages()
  }

  showNext() {
    if (!this.isOpen) return

    this.oldBackground = this.backgrounds[this.currentIndex]

    this.prevIndex = this.currentIndex
    this.currentIndex = this.currentIndex === this.gallery.length - 1 ? 0 : this.currentIndex + 1
    this.nextIndex = this.currentIndex === this.gallery.length - 1 ? 0 : this.currentIndex + 1

    this.images[this.prevIndex].style.display = 'none'

    this.currentBackgroud = this.backgrounds[this.currentIndex]

    this.updateImages()
  }

  updateImages() {
    this.lightBox.classList.add('wait')
    this.loadImage(this.gallery[this.currentIndex]).then(() => {
      this.lightBox.classList.remove('wait')
      this.currentZIndex ++
      this.imagesWrappers[this.currentIndex].style.zIndex = this.currentZIndex

      const oldBackground = this.oldBackground
      const currentBackgroud = this.currentBackgroud
      const currentImageWrapper = this.imagesWrappers[this.currentIndex]
      // const prevImageWrapper = this.imagesWrappers[this.prevIndex]

      const tl = gsap.timeline({
        onStart: () => {
          currentImageWrapper.style.display = 'block'
        }
        // onComplete: () => {
        //   if (!this.prevPagerIsActive) prevImageWrapper.style.display = 'none'
        // }
      })

      tl.addLabel('start', 0)

      if (oldBackground) {
        tl.to(oldBackground, {
          alpha: 0,
          duration: 0.8,
          ease: 'power2.out',
          onComplete: () => {
            if (oldBackground !== this.currentBackgroud) oldBackground.style.display = 'none'
          }
        }, 'start')
      }

      tl.to(currentBackgroud, {
        alpha: 1,
        duration: 0.8,
        ease: 'power2.out',
        onStart: () => {
          currentBackgroud.style.display = 'block'
        }
      }, 'start')

      this.images[this.currentIndex].style.display = 'block'
      this.images[this.currentIndex].style.opacity = 1

      tl.to([this.imagesWrappers[this.currentIndex], this.imagesReverses[this.currentIndex]], {
        x: 0,
        duration: 0.8,
        ease: 'power2.out'
      }, 'start')

      tl.to(this.images[this.currentIndex], {
        scale: 1,
        duration: 0.8,
        ease: 'power2.out'
      }, 'start')

      this.prevPagerIsActive = false
      this.nextPagerIsActive = false

      this.currentPosEvent && this.onMouseMove(this.currentPosEvent)
    })
  }

  loadImage(image) {
		return new Promise((resolve) => {
      const img = new Image()

      img.onload = () => {
        resolve(image)
      }

      img.src = image
    })
	}

  resize() {
    this.imageWidth = this.image.offsetWidth
    this.imageHeight = this.image.offsetHeight
  }
}



// WEBPACK FOOTER //
// ./scripts/util/LightBox.js